import { AsyncBoundary, cn, Row } from '@rematter/ui-web';
import { useAtom, useAtomValue } from 'jotai';
import { LucideBarChart3 } from 'lucide-react';

import {
  $remoteWarehouseCredential,
  $selectedEntitlement,
  $warehouseCredential,
  type EntitlementKey,
} from '../../_state/organizationEntitlements.state';
import { useTranslations } from '../../i18n/useTranslations';

import { ReportsEntitlementPanel } from './entitlements/ReportsEntitlementPanel';

const entitlementIcons: Record<EntitlementKey, React.ReactNode> = {
  reports: <LucideBarChart3 className="size-4" />,
};

const entitlementPanels: Record<EntitlementKey, React.FC> = {
  reports: ReportsEntitlementPanel,
};

//
// ⚛️ ⚛️ ⚛️ ORGANIZATION TAB ENTITLEMENTS ⚛️ ⚛️ ⚛️
//
export const OrganizationTabEntitlements = () => {
  const t = useTranslations();

  const [selectedEntitlement, setSelectedEntitlement] =
    useAtom($selectedEntitlement);
  const credential = useAtomValue($warehouseCredential);
  const { isLoading, error } = useAtomValue($remoteWarehouseCredential);

  const entitlements: {
    key: EntitlementKey;
    label: string;
    enabled: boolean;
  }[] = [
    {
      key: 'reports',
      label: t.entitlements.sidebar.reports,
      enabled: credential?.enabled ?? false,
    },
  ];

  const ActivePanel = entitlementPanels[selectedEntitlement];

  return (
    <Row className="@OrganizationTabEntitlements h-full">
      <div className="w-[250px] shrink-0 border-r border-solid border-neutral/10 bg-white">
        <div className="border-b border-solid border-neutral/10 px-3 py-2 text-xs text-neutral/40">
          {t.entitlements.sidebar.title}
        </div>
        {entitlements.map((item) => {
          const isSelected = item.key === selectedEntitlement;

          return (
            <button
              key={item.key}
              className={cn(
                'flex w-full items-center gap-2 px-3 py-2 text-left text-sm outline-none hover:bg-neutral/10',
                isSelected && 'bg-neutral/60 text-white hover:bg-neutral/60',
              )}
              onClick={(e) => {
                e.preventDefault();
                setSelectedEntitlement(item.key);
              }}
            >
              <span className={cn(isSelected && 'text-white')}>
                {entitlementIcons[item.key]}
              </span>
              <span className="flex-1">{item.label}</span>
              <span
                className={cn(
                  'rounded px-1.5 py-0.5 text-xs',
                  item.enabled && 'bg-success/10 text-success/80',
                  item.enabled && isSelected && 'bg-success/30 text-white',
                  !item.enabled && 'bg-neutral/10 text-neutral/40',
                  !item.enabled && isSelected && 'bg-white/20 text-white',
                )}
              >
                {item.enabled
                  ? t.entitlements.sidebar.enabled
                  : t.entitlements.sidebar.disabled}
              </span>
            </button>
          );
        })}
      </div>
      <div className="flex-1 overflow-auto">
        <AsyncBoundary loading={isLoading} error={error}>
          <ActivePanel />
        </AsyncBoundary>
      </div>
    </Row>
  );
};
